// Alarms tab - one board for every CloudWatch alarm Data Engineering owns.
//
// The problem it replaces: SNS mails on both the ALARM and the OK transition,
// so the mailbox shows "broken" and "recovered" in the same shape and neither
// tells you the current state. This board answers, in reading order:
//
//   1. Is anything broken right now?          -> verdict banner
//   2. What else deserves a look?             -> four triage tiles
//   3. What exactly, and where do I go?       -> grouped rows with deep links
//   4. Which alarms are filling my inbox?     -> the noise chip, sorted first
//
// Healthy alarms collapse by default. 34 of 38 rows being green is the normal
// case, and expanding it by default would bury the four that matter.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiHttpError, type ApiConfig, getAlarmBoard } from "../api";
import type { AlarmBoardResponse, AlarmRow, AlarmTriage } from "../types";

const POLL_INTERVAL_MS = 60000;

// Below this many state changes in the window an alarm is not worth calling out
// as noisy - one incident plus its recovery is normal, not a pattern.
const NOISE_FLOOR = 5;

const GROUPS: { key: AlarmTriage; label: string; blurb: string; startsOpen: boolean }[] = [
  {
    key: "firing",
    label: "Firing now",
    blurb: "Breached its threshold and has not recovered.",
    startsOpen: true,
  },
  {
    key: "no_data",
    label: "No data",
    blurb: "Cannot tell you whether it is broken. Usually an idle or undeployed resource.",
    startsOpen: true,
  },
  {
    key: "muted",
    label: "Will not email you",
    blurb: "Healthy, but its actions are disabled, so a breach would stay silent.",
    startsOpen: true,
  },
  {
    key: "ok",
    label: "Healthy",
    blurb: "Inside its threshold.",
    startsOpen: false,
  },
];

export function AlarmsPanel({ config }: { config: ApiConfig }) {
  const [payload, setPayload] = useState<AlarmBoardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<AlarmTriage>>(
    () => new Set(GROUPS.filter((g) => g.startsOpen).map((g) => g.key)),
  );

  const refresh = useCallback(async () => {
    try {
      setPayload(await getAlarmBoard(config));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoaded(true);
    }
  }, [config]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const toggle = useCallback((key: AlarmTriage) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Jump straight to one alarm: filter to it and open whichever group holds it,
  // so a click from the loudest-alarms strip cannot land on a collapsed group.
  const focus = useCallback((alarm: AlarmRow) => {
    setQuery(alarm.display_name);
    setOpen((prev) => new Set(prev).add(alarm.triage));
  }, []);

  // The alarms filling the inbox are usually healthy right now - that is what
  // flapping means - so they sit inside the collapsed Healthy group where the
  // operator would never find them. This strip pulls them to the top.
  const loudest = useMemo(() => {
    const rows = (payload?.data.alarms ?? []).filter(
      (a) => a.transitions !== null && a.transitions >= NOISE_FLOOR,
    );
    return rows.sort((a, b) => (b.transitions ?? 0) - (a.transitions ?? 0)).slice(0, 3);
  }, [payload]);

  const needle = query.trim().toLowerCase();
  const matched = useMemo(() => {
    const rows = payload?.data.alarms ?? [];
    if (!needle) return rows;
    return rows.filter(
      (a) =>
        a.name.toLowerCase().includes(needle) ||
        a.display_name.toLowerCase().includes(needle) ||
        (a.description ?? "").toLowerCase().includes(needle),
    );
  }, [payload, needle]);

  if (!loaded && !payload) return <div className="empty">Loading alarms...</div>;

  const summary = payload?.data.summary;
  const meta = payload?.meta;

  return (
    <div className="alarms-panel">
      {error && <div className="error-banner">{error}</div>}
      {payload?.note && <div className="alarms-note">{payload.note}</div>}

      {summary && meta && (
        <>
          <section
            className={"alarms-verdict alarms-verdict-" + (summary.firing > 0 ? "bad" : "good")}
            aria-live="polite"
          >
            <div className="alarms-verdict-main">
              <strong>
                {summary.firing > 0
                  ? `${summary.firing} alarm${summary.firing === 1 ? "" : "s"} firing`
                  : "Nothing is firing"}
              </strong>
              <span className="alarms-verdict-sub">
                {summary.total} Data Engineering alarm{summary.total === 1 ? "" : "s"} in{" "}
                {meta.region}
                {meta.alarms_excluded_not_de > 0 &&
                  ` (${meta.alarms_excluded_not_de} other-team alarms hidden)`}
              </span>
            </div>
            <div className="alarms-verdict-meta">
              <span title={meta.generated_at}>Checked {formatClock(meta.generated_at)}</span>
              <button className="alarms-refresh" onClick={() => void refresh()}>
                Refresh
              </button>
            </div>
          </section>

          <section className="alarms-tiles" aria-label="Alarm counts by state">
            {GROUPS.map((g) => (
              <Tile key={g.key} triage={g.key} label={g.label} value={summary[g.key]} />
            ))}
          </section>

          <div className="alarms-controls">
            <input
              className="alarms-search"
              type="search"
              placeholder="Filter by name or description"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Filter alarms"
            />
            <span className="alarms-controls-note">
              {meta.history_available
                ? `Email counts are state changes over the last ${meta.window_days} days.`
                : "Email counts unavailable: CloudWatch alarm history could not be read."}
            </span>
          </div>

          {loudest.length > 0 && (
            <div className="alarms-loudest">
              <span className="alarms-loudest-label">Most emails sent</span>
              {loudest.map((a) => (
                <button key={a.name} className="alarms-loudest-item" onClick={() => focus(a)}>
                  <span className="alarms-loudest-count">{a.transitions}</span>
                  {a.display_name}
                </button>
              ))}
            </div>
          )}

          {summary.missing_description > 0 && (
            <div className="alarms-gap">
              {summary.missing_description} alarm
              {summary.missing_description === 1 ? " has" : "s have"} no description in
              CloudWatch, so {summary.missing_description === 1 ? "its" : "their"} row shows the
              raw threshold rule instead of an explanation.
            </div>
          )}

          {GROUPS.map((g) => {
            const rows = matched.filter((a) => a.triage === g.key);
            if (rows.length === 0) return null;
            const isOpen = open.has(g.key);
            return (
              <section key={g.key} className={"alarm-group alarm-group-" + g.key}>
                <button
                  className="alarm-group-head"
                  onClick={() => toggle(g.key)}
                  aria-expanded={isOpen}
                >
                  <span className={"tree-toggle" + (isOpen ? "" : " tree-toggle-collapsed")} />
                  <span className="alarm-group-label">{g.label}</span>
                  <span className="alarm-group-count">{rows.length}</span>
                  <span className="alarm-group-blurb">{g.blurb}</span>
                </button>
                {isOpen && (
                  <div className="alarm-rows">
                    {rows.map((a) => (
                      <AlarmRowView key={a.name} alarm={a} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {matched.length === 0 && needle && (
            <div className="empty">No alarm matches "{query}".</div>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ triage, label, value }: { triage: AlarmTriage; label: string; value: number }) {
  return (
    <div className={"alarm-tile alarm-tile-" + triage + (value === 0 ? " alarm-tile-zero" : "")}>
      <span className="alarm-tile-value">{value}</span>
      <span className="alarm-tile-label">{label}</span>
    </div>
  );
}

function AlarmRowView({ alarm }: { alarm: AlarmRow }) {
  // Prefer the operator-written CloudWatch description; fall back to the
  // threshold rule reconstructed server-side so a row is never blank.
  const explanation = alarm.description ?? alarm.condition;
  return (
    <div className={"alarm-row alarm-row-" + alarm.triage}>
      <div className="alarm-row-main">
        <div className="alarm-row-title">
          <span className={"alarm-state alarm-state-" + alarm.triage}>
            {stateLabel(alarm.state)}
          </span>
          <strong className="alarm-row-name" title={alarm.name}>
            {alarm.display_name}
          </strong>
          {alarm.scope === "global" && <span className="alarm-scope">platform-wide</span>}
        </div>
        <p className="alarm-row-desc">{explanation}</p>
        {alarm.description && <p className="alarm-row-cond">{alarm.condition}</p>}
        {alarm.triage === "firing" && alarm.state_reason && (
          <p className="alarm-row-reason">{alarm.state_reason}</p>
        )}
      </div>

      <div className="alarm-row-side">
        <div className="alarm-chips">
          {!alarm.notifies.enabled && <span className="alarm-chip alarm-chip-warn">muted</span>}
          {alarm.notifies.enabled && alarm.notifies.on_recovery && (
            <span
              className="alarm-chip"
              title="Emails on both the ALARM and the OK transition, so each incident produces two mails."
            >
              mails twice
            </span>
          )}
          {alarm.notifies.enabled && !alarm.notifies.on_alarm && (
            <span className="alarm-chip alarm-chip-warn">no email target</span>
          )}
          {alarm.transitions !== null && alarm.transitions > 0 && (
            <span
              className={
                "alarm-chip alarm-chip-noise" + (alarm.transitions >= 10 ? " alarm-chip-loud" : "")
              }
              title="State changes in the reporting window. Each one sent you a mail."
            >
              {alarm.transitions} state changes
            </span>
          )}
        </div>
        <div className="alarm-links">
          <a href={alarm.console_url} target="_blank" rel="noreferrer">
            Alarm
          </a>
          {alarm.watches && (
            <a href={alarm.watches.url} target="_blank" rel="noreferrer" title={alarm.watches.label}>
              {resourceLabel(alarm.watches.kind)}
            </a>
          )}
        </div>
        {alarm.state_since && (
          <span className="alarm-since" title={alarm.state_since}>
            since {formatSince(alarm.state_since)}
          </span>
        )}
      </div>
    </div>
  );
}

// The AWS service names the link goes to. Unknown kinds fall back to the raw
// token rather than being dropped, so a new dimension type still gets a link.
const RESOURCE_LABELS: Record<string, string> = {
  lambda: "Lambda",
  states: "Step Functions",
  dynamodb: "DynamoDB",
  logs: "Log group",
  athena: "Athena",
  sqs: "Queue",
  apigateway: "API Gateway",
};

function resourceLabel(kind: string): string {
  return RESOURCE_LABELS[kind] ?? kind;
}

function stateLabel(state: AlarmRow["state"]): string {
  if (state === "ALARM") return "ALARM";
  if (state === "INSUFFICIENT_DATA") return "NO DATA";
  return "OK";
}

function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hours = (Date.now() - d.getTime()) / 3_600_000;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function formatError(e: unknown): string {
  if (e instanceof ApiHttpError) {
    return `${e.status} ${e.code}: ${e.message}${e.requestId ? ` (req ${e.requestId})` : ""}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
